import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Card,
  Modal,
  Group,
  Text,
  Flex,
  TextInput,
  SegmentedControl,
  Title,
  ActionIcon,
  Badge,
} from "@mantine/core";
import { IconCircle, IconMedal, IconPencil } from "@tabler/icons-react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { MilestoneItem, MilestoneTrigger } from "./";
import { PaginatedList } from "@components/shared";

export default function MilestoneList({
  items = null,
  requestInfo = {
    url: `/retrieve-milestones/`,
    data: {},
  },
  refresh,
}) {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const [refresh2, setRefresh2] = useState(false);
  const [filters, setFilters] = useState({
    scope: "entire",
    status: "entire",
    variety: "entire",
  });

  const reqData = {
    ...filters,
    ...requestInfo.data,
    search_value: searchValue,
    refresh,
    refresh2,
    pagination: true,
  };

  return (
    <div>
      {requestInfo && (
        <React.Fragment>
          <TextInput
            placeholder={t("common:search")}
            size="lg"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            mb="sm"
          />
          <Flex gap="xs">
            <SegmentedControl
              value={filters.status}
              data={[
                { label: t("common:all"), value: "entire" },
                { label: t("common:active"), value: "active" },
                { label: t("common:inactive"), value: "inactive" },
              ]}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  status: e,
                })
              }
            />
            <SegmentedControl
              value={filters.scope}
              data={[
                { label: t("common:all"), value: "entire" },
                { label: t("mixer:milestone:current"), value: "current" },
                {
                  label: t("mixer:milestone:pickup"),
                  value: "requires_pickup",
                },
                { label: t("mixer:milestone:ongoing"), value: "ongoing" },
                { label: t("mixer:milestone:virtual"), value: "virtual" },
              ]}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  scope: e,
                })
              }
            />
            <SegmentedControl
              value={filters.variety}
              data={[
                { label: t("common:all"), value: "entire" },
                { label: "Mileage", value: "mileage" },
                { label: "Odometer Readings", value: "odometer_readings" },
                {
                  label: "Unique Dealer Visits",
                  value: "unique_dealer_visits",
                },
                { label: "Keyword Claims", value: "keyword_claims" },
                { label: "Check-Ins", value: "check_ins" },
              ]}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  variety: e,
                })
              }
            />
          </Flex>
        </React.Fragment>
      )}
      <PaginatedList
        tableData={items}
        requestInfo={{
          url: requestInfo.url,
          data: reqData,
        }}
        responseMapping={(r) =>
          r.map((m) => ({
            ...m,
            link_url: `/milestones/${m.id}`,
          }))
        }
        displayFormatter={(item) => (
          <Card
            // component={Link}
            // to={item.original.link_url}
            key={item.original.id}
          >
            <Flex gap="md" direction={{ base: "column", md: "row" }}>
              <div>
                {item.original.filename_url ? (
                  <StyledImage
                    src={item.original.filename_url}
                    height={100}
                    width={100}
                  />
                ) : (
                  <StyledPlaceholder height={100} width={100}>
                    <IconMedal size={24} color="var(--mantine-color-gray-6)" />
                  </StyledPlaceholder>
                )}
              </div>
              <div style={{ flexGrow: 1 }}>
                <Flex align="center" gap="xs">
                  <Text fw={700}>{item.original.title}</Text>
                  {item.original.status && (
                    <IconCircle
                      size={16}
                      color={
                        item.original.status === 1
                          ? "var(--mantine-color-green-5)"
                          : "var(--mantine-color-red-5)"
                      }
                      fill={
                        item.original.status === 1
                          ? "var(--mantine-color-green-5)"
                          : "var(--mantine-color-red-5)"
                      }
                    />
                  )}
                  <MilestoneEdit
                    info={item.original}
                    id={item.original.id}
                    onSuccess={() => setRefresh2(!refresh2)}
                  />
                  <MilestoneTrigger
                    info={item.original}
                    id={item.original.id}
                    fetchData={() => setRefresh2(!refresh2)}
                    triggers={item.original.triggers}
                  />
                </Flex>
                <Text size="sm" c="dimmed">
                  {item.original.description}
                </Text>
                {item.original.start_at_for_form && (
                  <Group gap="xs">
                    {/* <div>{entityIcon.campaign()}</div> */}
                    <Text size="sm" c="dimmed">
                      {item.original.start_at_for_form} to{" "}
                      {item.original.end_at_for_form}
                    </Text>
                  </Group>
                )}
                <Box mt="xs">
                  <Flex gap="xs">
                    <Badge size="xs" variant="light" color="gray">
                      {varietyUnits[item.original.variety]}
                    </Badge>
                    <Badge
                      size="xs"
                      variant="light"
                      color={item.original.requires_pickup ? "orange" : "blue"}
                    >
                      {item.original.requires_pickup
                        ? t("mixer:milestone:requires_pickup")
                        : t("mixer:milestone:virtual")}
                    </Badge>
                  </Flex>
                </Box>
              </div>
              <div>
                <Title order={2} align="right" fw={600}>
                  {item.original.value}
                </Title>
                <Text align="right" size="xs" c="dimmed">
                  {varietyUnits[item.original.variety]}
                </Text>
              </div>
            </Flex>
          </Card>
        )}
      />
    </div>
  );
}

const MilestoneEdit = ({ info, id, onSuccess }) => {
  const [isOpen, setOpen] = useState(false);
  if (!info) return null;

  function onClose() {
    setOpen(false);
    onSuccess();
  }

  return (
    <div>
      <ActionIcon
        color="gray"
        size="xs"
        variant="light"
        radius="xl"
        onClick={() => setOpen(true)}
      >
        <IconPencil size={14} />
      </ActionIcon>
      <Modal opened={isOpen} onClose={onClose}>
        <MilestoneItem info={info} onSuccess={onClose} id={id} />
      </Modal>
    </div>
  );
};

const StyledPlaceholder = styled.div`
  width: ${(props) => (props.width ? `${props.width}px` : `100px`)};
  height: ${(props) => (props.height ? `${props.height}px` : `100px`)};
  background: var(--mantine-color-gray-4);
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledImage = styled.img`
  width: ${(props) => (props.width ? `${props.width}px` : `100px`)};
  height: ${(props) => (props.height ? `${props.height}px` : `100px`)};
  object-fit: cover;
  border-radius: 5px;
`;

const varietyUnits = {
  1: "miles",
  2: "readings",
  3: "unique dealer visits",
  4: "keyword claims",
};
